import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User } from '../../../core/types';
import { ROUTES } from '../../../core/data/routes';
import { Dialog, LogoSvg } from '../../../core/components/ui';
import { useHomepageStore } from '../store/homepageStore';
import { useAuthStore } from '../../auth/store/authStore';

interface SidebarNavProps {
	user: User;
	onClose: () => void;
	open?: boolean;
	currentTab?: number;
	onTabChange?: (index: number) => void;
}

const SidebarNav: React.FC<SidebarNavProps> = ({
	user,
	onClose,
	open = false,
	currentTab = 0,
	onTabChange,
}) => {
	const navigate = useNavigate();
	const homepageStore = useHomepageStore();
	const authStore = useAuthStore();
	const [credits, setCredits] = useState<number>(0);
	const [desktop, setDesktop] = useState(() => window.innerWidth >= 1024);
	const location = useLocation();
	const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
	const initials = (first: string, last: string) =>
		`${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();

	useEffect(() => {
		homepageStore.getSessionCredit().then((c) => setCredits(c.quantity || 0));
	}, []);

	useEffect(() => {
		const onResize = () => setDesktop(window.innerWidth >= 1024);
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	const handleClose = () => {
		if (!desktop) onClose();
	};

	const handleLogout = () => {
		authStore.logout();
		handleClose();
		navigate(ROUTES.LOGIN);
	};

	// Tab-based items (switch content inside IndexPage)
	const tabItems = [
		{ label: 'Book Session', icon: PeopleIcon, tab: 0 },
		{ label: 'Upcoming Sessions', icon: CalendarIcon, tab: 1 },
		{ label: 'Assessments', icon: AssignmentIcon, tab: 2 },
	];

	// Route-based items (navigate to a new page)
	const routeItems = [
		{
			label: 'Payment History',
			icon: PaymentIcon,
			route: ROUTES.PAYMENT_HISTORY2,
		},
		{ label: 'Buy Sessions Credits', icon: CartIcon, route: ROUTES.SESSION },
		{ label: 'Settings', icon: SettingsIcon, tab: 3 },
		// {
		// 	label: 'Delete Account',
		// 	icon: DeleteIcon,
		// 	route: ROUTES.DELETE_ACCOUNT,
		// 	danger: true,
		// },
	];

	const allItems: NavItem[] = [
		...tabItems.map((i) => ({ ...i, mobileHidden: true })),
		...routeItems,
	];

	return (
		<>
			{/* Overlay — only on mobile/tablet */}
			{!desktop && (
				<div
					className={`drawer-overlay${open ? ' drawer-overlay--open' : ''}`}
					onClick={onClose}
				/>
			)}

			<aside className={`app-sidebar${open ? ' app-sidebar--open' : ''}`}>
				{/* ── Header ── */}
				<div style={{ padding: '24px 20px 0' }}>
					{/* Logo */}
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 10,
							marginBottom: 24,
						}}>
						<LogoSvg size={36} />
						<span
							style={{
								fontSize: 17,
								fontWeight: 700,
								color: 'var(--color-primary)',
							}}>
							TherApp <span style={{ opacity: 0.45 }}>Connect</span>
						</span>
					</div>

					{/* Profile card */}
					<div
						style={{
							padding: 16,
							borderRadius: 16,
							background: 'var(--color-surface-container)',
							display: 'flex',
							alignItems: 'center',
							gap: 12,
						}}>
						<div
							style={{
								width: 44,
								height: 44,
								borderRadius: '50%',
								background: 'var(--color-primary)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								color: 'white',
								fontWeight: 700,
								fontSize: 14,
								flexShrink: 0,
							}}>
							{initials(user.firstName, user.lastName)}
						</div>
						<div style={{ overflow: 'hidden' }}>
							<div
								style={{
									fontWeight: 700,
									fontSize: 14,
									color: 'var(--color-primary)',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}>
								{user.firstName} {user.lastName}
							</div>
							<div
								style={{
									fontSize: 12,
									color: 'var(--color-on-surface-variant)',
									marginTop: 2,
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}>
								{user.email || 'No email'}
							</div>
						</div>
					</div>

					{/* Credits strip */}
					<div
						style={{
							marginTop: 16,
							padding: '16px 20px',
							background: 'var(--color-primary)',
							borderRadius: 16,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}>
						<div>
							<div
								style={{
									fontSize: 11,
									fontWeight: 600,
									letterSpacing: 0.8,
									color: 'rgba(255,255,255,0.6)',
									textTransform: 'uppercase',
								}}>
								Credits
							</div>
							<div
								style={{
									fontSize: 28,
									fontWeight: 800,
									color: 'white',
									lineHeight: 1.1,
								}}>
								{credits}
							</div>
						</div>
						<button
							onClick={() => {
								navigate(ROUTES.SESSION);
								handleClose();
							}}
							style={{
								padding: '8px 16px',
								borderRadius: 10,
								background: 'rgba(255,255,255,0.15)',
								color: 'white',
								fontWeight: 600,
								fontSize: 13,
							}}>
							+ Buy
						</button>
					</div>
				</div>

				{/* ── Nav items ── */}
				{/* ── Nav items ── */}
				<nav
					style={{
						flex: 1,
						padding: '16px 12px',
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
					}}>
					{allItems
						.filter((item) => desktop || !item.mobileHidden)
						.map((item) => {
							const isTabItem = 'tab' in item && item.tab !== undefined;
							const isActive = isTabItem
								? item.tab === currentTab
								: location.pathname === (item as any).route; // ← CHANGED

							return (
								<button
									key={item.label}
									onClick={() => {
										if (isTabItem) {
											onTabChange?.(item.tab!);
										} else {
											onTabChange?.(-1); // ✅ clear tab highlight when going to a route
											navigate((item as any).route);
										}
										handleClose();
									}}
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: 14,
										padding: '12px 16px',
										borderRadius: 12,
										background: isActive
											? 'var(--color-surface-container)'
											: 'transparent',
										color: item.danger
											? 'var(--color-error)'
											: isActive
												? 'var(--color-primary)'
												: 'var(--color-on-surface)',
										fontSize: 14,
										fontWeight: isActive ? 700 : 500,
										width: '100%',
										textAlign: 'left',
										transition: 'background 0.15s',
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.background =
											'var(--color-surface-container)';
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.background = isActive
											? 'var(--color-surface-container)'
											: 'transparent';
									}}>
									<item.icon
										size={20}
										color={
											item.danger
												? 'var(--color-error)'
												: isActive
													? 'var(--color-primary)'
													: 'var(--color-on-surface-variant)'
										}
									/>
									{item.label}
								</button>
							);
						})}
				</nav>

				{/* ── Logout ── */}
				<div style={{ padding: '0 12px 24px' }}>
					<Dialog
						open={logoutDialogOpen}
						onClose={() => setLogoutDialogOpen(false)}>
						<div
							style={{
								position: 'relative',
								zIndex: 9999,
								pointerEvents: 'auto',
							}}>
							<h3
								style={{
									fontSize: 17,
									fontWeight: 700,
									marginBottom: 8,
								}}>
								Log Out
							</h3>

							<p
								style={{
									fontSize: 13,
									color: 'var(--color-on-surface-variant)',
									marginBottom: 20,
								}}>
								Are you sure you want to log out?
							</p>

							<div style={{ display: 'flex', gap: 10 }}>
								<button
									type='button'
									onClick={() => setLogoutDialogOpen(false)}
									style={{
										flex: 1,
										height: 44,
										borderRadius: 8,
										background: 'var(--color-surface-variant)',
										color: 'var(--color-on-surface-variant)',
										fontWeight: 600,
										cursor: 'pointer',
									}}>
									Cancel
								</button>

								<button
									type='button'
									onClick={() => {
										setLogoutDialogOpen(false);
										handleLogout();
									}}
									style={{
										flex: 1,
										height: 44,
										borderRadius: 8,
										background: '#ba071f',
										color: 'white',
										fontWeight: 700,
										cursor: 'pointer',
									}}>
									Log Out
								</button>
							</div>
						</div>
					</Dialog>
					<button
						onClick={() => setLogoutDialogOpen(true)}
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 14,
							padding: '12px 16px',
							borderRadius: 12,
							background: 'var(--color-error-container)',
							color: 'var(--color-error)',
							fontSize: 14,
							fontWeight: 600,
							width: '100%',
							textAlign: 'left',
						}}>
						<LogoutIcon
							size={20}
							color='var(--color-error)'
						/>
						Log Out
					</button>
				</div>
			</aside>
		</>
	);
};

// ── Types ──────────────────────────────────────────────────────
type NavItem = {
	label: string;
	icon: React.FC<{ size: number; color: string }>;
	tab?: number;
	route?: string;
	danger?: boolean;
	mobileHidden?: boolean;
};

// ── Icons ──────────────────────────────────────────────────────
const PeopleIcon: React.FC<{ size: number; color: string }> = ({
	size,
	color,
}) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill='none'
		stroke={color}
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'>
		<path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' />
		<circle
			cx='9'
			cy='7'
			r='4'
		/>
		<path d='M23 21v-2a4 4 0 0 0-3-3.87' />
		<path d='M16 3.13a4 4 0 0 1 0 7.75' />
	</svg>
);
const CalendarIcon: React.FC<{ size: number; color: string }> = ({
	size,
	color,
}) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill='none'
		stroke={color}
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'>
		<rect
			x='3'
			y='4'
			width='18'
			height='18'
			rx='2'
			ry='2'
		/>
		<line
			x1='16'
			y1='2'
			x2='16'
			y2='6'
		/>
		<line
			x1='8'
			y1='2'
			x2='8'
			y2='6'
		/>
		<line
			x1='3'
			y1='10'
			x2='21'
			y2='10'
		/>
	</svg>
);
const AssignmentIcon: React.FC<{ size: number; color: string }> = ({
	size,
	color,
}) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill='none'
		stroke={color}
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'>
		<path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
		<polyline points='14 2 14 8 20 8' />
		<line
			x1='16'
			y1='13'
			x2='8'
			y2='13'
		/>
		<line
			x1='16'
			y1='17'
			x2='8'
			y2='17'
		/>
	</svg>
);
const PaymentIcon: React.FC<{ size: number; color: string }> = ({
	size,
	color,
}) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill='none'
		stroke={color}
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'>
		<rect
			x='1'
			y='4'
			width='22'
			height='16'
			rx='2'
			ry='2'
		/>
		<line
			x1='1'
			y1='10'
			x2='23'
			y2='10'
		/>
	</svg>
);
const CartIcon: React.FC<{ size: number; color: string }> = ({
	size,
	color,
}) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill='none'
		stroke={color}
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'>
		<circle
			cx='9'
			cy='21'
			r='1'
		/>
		<circle
			cx='20'
			cy='21'
			r='1'
		/>
		<path d='M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6' />
	</svg>
);
const SettingsIcon: React.FC<{ size: number; color: string }> = ({
	size,
	color,
}) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill='none'
		stroke={color}
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'>
		<circle
			cx='12'
			cy='12'
			r='3'
		/>
		<path d='M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z' />
	</svg>
);
const DeleteIcon: React.FC<{ size: number; color: string }> = ({
	size,
	color,
}) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill='none'
		stroke={color}
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'>
		<polyline points='3 6 5 6 21 6' />
		<path d='M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2' />
	</svg>
);
const LogoutIcon: React.FC<{ size: number; color: string }> = ({
	size,
	color,
}) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill='none'
		stroke={color}
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'>
		<path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' />
		<polyline points='16 17 21 12 16 7' />
		<line
			x1='21'
			y1='12'
			x2='9'
			y2='12'
		/>
	</svg>
);

export default SidebarNav;
